/**
 * Utility functions for managing unique node labels in workflows.
 * Prevents duplicate node labels which can cause issues with expression resolution.
 */

import type { Node } from 'reactflow';

/**
 * Extracts the base label and numeric suffix from a node label.
 * Examples:
 *   "Azure Blob Storage" -> { base: "Azure Blob Storage", suffix: null }
 *   "Azure Blob Storage 2" -> { base: "Azure Blob Storage", suffix: 2 }
 *   "Azure Blob Storage 10" -> { base: "Azure Blob Storage", suffix: 10 }
 */
function parseLabel(label: string): { base: string; suffix: number | null } {
  // Match a trailing space followed by a number at the end
  const match = label.match(/^(.+?)\s+(\d+)$/);
  if (match) {
    return { base: match[1], suffix: parseInt(match[2], 10) };
  }
  return { base: label, suffix: null };
}

/**
 * Generates a unique node label by appending a numeric suffix if needed.
 *
 * @param baseLabel - The desired label (e.g., "Azure Blob Storage")
 * @param existingLabels - Array of labels already in use
 * @param excludeLabel - Optional label to exclude from conflict check (for rename scenarios)
 * @returns A unique label (e.g., "Azure Blob Storage 2" if base is taken)
 *
 * Examples:
 *   generateUniqueNodeLabel("Code", []) -> "Code"
 *   generateUniqueNodeLabel("Code", ["Code"]) -> "Code 2"
 *   generateUniqueNodeLabel("Code", ["Code", "Code 2"]) -> "Code 3"
 *   generateUniqueNodeLabel("Code 2", ["Code", "Code 2"]) -> "Code 3"
 */
export function generateUniqueNodeLabel(
  baseLabel: string,
  existingLabels: string[],
  excludeLabel?: string
): string {
  // Filter out the excluded label (for rename scenarios where we don't want to conflict with ourselves)
  const labelsToCheck = excludeLabel
    ? existingLabels.filter(l => l !== excludeLabel)
    : existingLabels;

  // If the base label is not taken, use it as-is
  if (!labelsToCheck.includes(baseLabel)) {
    return baseLabel;
  }

  // Parse the base label to extract any existing suffix
  const { base } = parseLabel(baseLabel);

  // Find all labels that match the base pattern (with or without suffix)
  const matchingLabels = labelsToCheck.filter(label => {
    const parsed = parseLabel(label);
    return parsed.base === base;
  });

  // Find the highest suffix in use
  let maxSuffix = 1; // Start at 1 because the base label (no suffix) counts as "1"
  for (const label of matchingLabels) {
    const parsed = parseLabel(label);
    if (parsed.suffix !== null && parsed.suffix >= maxSuffix) {
      maxSuffix = parsed.suffix;
    }
  }

  // Return with the next available suffix
  return `${base} ${maxSuffix + 1}`;
}

/**
 * Extracts all node labels from a list of nodes.
 *
 * @param nodes - Array of React Flow nodes
 * @returns Array of labels
 */
export function getNodeLabels(nodes: Node[]): string[] {
  return nodes
    .map(node => node.data?.label as string)
    .filter(Boolean);
}

/**
 * Validates if a label is unique among existing nodes.
 *
 * @param label - The label to validate
 * @param existingLabels - Array of labels already in use
 * @param currentLabel - The current label (for rename scenarios, to allow keeping the same name)
 * @returns true if the label is unique or unchanged, false if it would create a duplicate
 */
export function isLabelUnique(
  label: string,
  existingLabels: string[],
  currentLabel?: string
): boolean {
  // If the label hasn't changed, it's valid
  if (currentLabel && label === currentLabel) {
    return true;
  }

  return !existingLabels.includes(label);
}

/**
 * Gets a validation message for a duplicate label.
 *
 * @param label - The duplicate label
 * @returns A user-friendly error message
 */
export function getDuplicateLabelMessage(label: string): string {
  return `A node named "${label}" already exists. Labels must be unique for expressions to work correctly.`;
}
